import React, { useId } from 'react';

type IconProps = {
  className?: string;
  title?: string;
  size?: number;
};

const BaseIcon: React.FC<IconProps & { children: React.ReactNode; strokeWidth?: number }> = ({
  className = '',
  title,
  size = 24,
  strokeWidth = 1.6,
  children
}) => {
  const dimension = `${size}px`;
  const combinedClassName = ['flex-shrink-0', className].filter(Boolean).join(' ');

  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={combinedClassName}
      width={size}
      height={size}
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      vectorEffect="non-scaling-stroke"
      style={{ minWidth: dimension, minHeight: dimension, display: 'block', color: 'currentColor' }}
    >
      {children}
    </svg>
  );
};

export const CurrencyIcon: React.FC<IconProps> = ({ className = 'icon-md', title, size = 24 }) => (
  <BaseIcon className={`${className} icon-stroke text-brand-secondary`} title={title} size={size}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
    <path d="M12 7v10" stroke="currentColor" />
    <path d="M9 9.75c.9-1 2.1-1.5 3.75-1.5 1.77 0 3.25.9 3.25 2.5s-1.48 2.5-3.25 2.5-3.25.9-3.25 2.5 1.48 2.5 3.25 2.5c1.65 0 2.85-.5 3.75-1.5" stroke="currentColor" />
  </BaseIcon>
);

export const PeopleIcon: React.FC<IconProps> = ({ className = 'icon-md', title, size = 24 }) => (
  <BaseIcon className={`${className} icon-stroke text-brand-secondary`} title={title} size={size}>
    <circle cx="9" cy="9" r="3" stroke="currentColor" fill="none" />
    <circle cx="17" cy="9.5" r="2.5" stroke="currentColor" fill="none" />
    <path d="M4.5 19c.7-2.8 3.1-4 6-4s5.3 1.2 6 4" stroke="currentColor" />
    <path d="M14.5 15c2.35 0 4.2 1 5 3" stroke="currentColor" />
  </BaseIcon>
);

export const LocationIcon: React.FC<IconProps> = ({ className = 'icon-md', title, size = 24 }) => (
  <BaseIcon className={`${className} icon-stroke text-brand-secondary`} title={title} size={size}>
    <path d="M12 21s-6-5.4-6-10a6 6 0 0 1 12 0c0 4.6-6 10-6 10Z" stroke="currentColor" fill="none" />
    <circle cx="12" cy="11" r="2.5" stroke="currentColor" fill="none" />
  </BaseIcon>
);

export const CalendarIcon: React.FC<IconProps> = ({ className = 'icon-sm', title, size = 20 }) => (
  <BaseIcon className={`${className} icon-stroke text-brand-secondary`} title={title} size={size}>
    <rect x="4" y="5" width="16" height="15" rx="2.5" stroke="currentColor" fill="none" />
    <path d="M8 3v4" stroke="currentColor" />
    <path d="M16 3v4" stroke="currentColor" />
    <path d="M4 10h16" stroke="currentColor" />
    <rect x="8.5" y="12" width="3.5" height="3.5" rx="0.75" fill="currentColor" stroke="none" />
  </BaseIcon>
);

export const BoxIcon: React.FC<IconProps> = ({ className = 'icon-sm', title, size = 20 }) => (
  <BaseIcon className={`${className} icon-stroke text-brand-secondary`} title={title} size={size}>
    <path d="M4.5 8.5 12 12l7.5-3.5" stroke="currentColor" />
    <path d="M5 8.2v7.2c0 .7.4 1.3 1 1.6l5.5 2.6a2 2 0 0 0 1.7 0L18.7 17c.6-.3 1-1 1-1.6V8.2a2 2 0 0 0-1-1.6L12.7 4a2 2 0 0 0-1.7 0L6 6.6a2 2 0 0 0-1 1.6Z" stroke="currentColor" fill="none" />
  </BaseIcon>
);

export const PinDotIcon: React.FC<IconProps> = ({ className = 'icon-dot', title, size = 12 }) => {
  const gradientId = useId();

  return (
    <BaseIcon className={className} title={title} size={size} strokeWidth={0}>
      <circle cx="12" cy="12" r="11" fill={`url(#${gradientId})`} />
      <defs>
        <radialGradient id={gradientId} cx="50%" cy="40%" r="70%">
          <stop offset="0%" stopColor="rgba(30,109,59,0.65)" />
          <stop offset="100%" stopColor="rgba(30,109,59,0)" />
        </radialGradient>
      </defs>
    </BaseIcon>
  );
};
